/**
 * The hold clock piece: its behavior half reads the shared hold (useWebHold),
 * its standard look draws the line under the summary and the app's pre-warning
 * banner. Holds no rule of its own.
 */
import { PreWarnBanner } from '@/components/SessionRecovery/PreWarnBanner';
import { reservedLine } from '@/pages/booking/shared/bookingRules';

import { partsOf, pieceRoot, usePieceOptions, type PieceRuntime } from '../../contract/runtime';
import { formatSlotTime, getClientTimezone } from '../../format';
import type { WebHold } from '../../hooks/useWebHold';
import { Icon } from '../../ui/Icon';
import { contract, type HoldClockActions, type HoldClockState } from './contract';
import css from './HoldClock.module.css';

const part = partsOf(contract);

export type HoldClockRuntime = PieceRuntime<typeof contract, HoldClockState, HoldClockActions>;

export function useHoldClock(hold: WebHold, kind: 'service' | 'event'): HoldClockRuntime {
  const options = usePieceOptions(contract, undefined);
  const until = hold.expiresAt ? formatSlotTime(hold.expiresAt, getClientTimezone()) : null;
  return {
    state: {
      until: until ? reservedLine(kind, until) : null,
      warning: hold.phase === 'warn',
      keeping: hold.keeping,
      keepFailed: hold.keepFailed,
    },
    actions: { keep: hold.keep },
    options,
  };
}

/** The line under the summary. */
export function HoldClockLine({ state }: HoldClockRuntime) {
  if (!state.until) return null;
  return (
    <p className={css.hold} {...pieceRoot(contract)} {...part('until')}>
      <Icon name="clock" small />
      {state.until}
    </p>
  );
}

/** The pre-warning banner: pinned over the page on the phone, floating over the column's top on the desktop (`inline`). */
export function HoldClockWarning({ state, actions, inline = false }: HoldClockRuntime & { inline?: boolean }) {
  if (!state.warning) return null;
  return (
    <div className={`${css.warning} ${inline ? css.over : ''}`} {...pieceRoot(contract)} {...part('warning')}>
      <PreWarnBanner onKeep={actions.keep} keeping={state.keeping} errored={state.keepFailed} inline={inline} />
    </div>
  );
}
